const MENSAJES_POR_CODIGO = {
  credentials:
    "Usuario o contraseña incorrectos. Verifica tus datos e inténtalo de nuevo.",
  throttle:
    "Has superado el número máximo de intentos. Espera un momento e inténtalo de nuevo.",
  account_locked:
    "Tu cuenta está bloqueada por demasiados intentos fallidos. Inténtalo de nuevo en 45 minutos.",
  csrf:
    "La sesión de seguridad expiró. Recarga la página (F5) e inténtalo de nuevo.",
  network:
    "No fue posible conectar con el servidor. Revisa tu conexión o la URL del API.",
  server:
    "El servidor respondió con un error temporal. Inténtalo de nuevo en unos minutos.",
  account_inactive:
    "Tu cuenta está inactiva. Comunícate con un administrador del sistema.",
  password_expired:
    "Ha pasado mucho tiempo sin actualizar la contraseña. Debes cambiarla para continuar.",
  session_load_failed:
    "El inicio de sesión fue exitoso, pero no pudimos cargar tu perfil. Recarga la página e inténtalo de nuevo.",
  two_factor_invalid:
    "El código de verificación no es válido. Revise la app autenticadora e inténtelo de nuevo.",
  two_factor_recovery_invalid:
    "El código de recuperación no es válido. Inténtelo de nuevo.",
  unknown:
    "No fue posible completar el inicio de sesión. Inténtalo de nuevo.",
};

function formatoEspera(segundos) {
  const s = Number(segundos);
  if (!Number.isFinite(s) || s <= 0) return null;
  if (s >= 60) {
    const minutos = Math.max(1, Math.ceil(s / 60));
    return minutos === 1 ? "1 minuto" : `${minutos} minutos`;
  }
  return s === 1 ? "1 segundo" : `${s} segundos`;
}

function primerErrorCampo(errors, campo) {
  const lista = errors && errors[campo];
  if (!Array.isArray(lista) || !lista.length) return null;
  return String(lista[0] || "").trim() || null;
}

function codigoDesdeErrorEmail(valor) {
  if (!valor) return null;
  const v = String(valor).toLowerCase();
  if (v === "account_inactive" || v.includes("inactiv")) return "account_inactive";
  if (v === "password_expired" || v.includes("password_expired")) {
    return "password_expired";
  }
  if (v.includes("throttle") || v.includes("too many")) return "throttle";
  return null;
}

/**
 * Clasifica errores de login sin confundir throttle, CSRF o servidor con credenciales.
 * @returns {{ code: string, message: string, retryAfter: number|null }}
 */
export function parseLoginError(error) {
  if (error && error.code === "user_empty") {
    return {
      code: "session_load_failed",
      message: MENSAJES_POR_CODIGO.session_load_failed,
      retryAfter: null,
    };
  }

  const response = error && error.response;
  if (!response) {
    return {
      code: "network",
      message: MENSAJES_POR_CODIGO.network,
      retryAfter: null,
    };
  }

  const status = response.status;
  const data = response.data || {};
  const retryHeader =
    response.headers &&
    (response.headers["retry-after"] || response.headers["Retry-After"]);
  const retryFromBody =
    data.retry_after != null ? parseInt(String(data.retry_after), 10) : NaN;
  const retryParsed =
    retryHeader != null ? parseInt(String(retryHeader), 10) : NaN;
  const retryAfter = !Number.isNaN(retryFromBody) && retryFromBody > 0
    ? retryFromBody
    : !Number.isNaN(retryParsed) && retryParsed > 0
      ? retryParsed
      : null;

  if (
    status === 429 ||
    data.error_code === "throttle" ||
    data.error_code === "account_locked"
  ) {
    const code =
      data.error_code === "account_locked" ? "account_locked" : "throttle";
    const espera = formatoEspera(retryAfter);
    let message =
      (data.message && String(data.message).trim()) ||
      MENSAJES_POR_CODIGO[code];
    if (!data.message && espera) {
      message =
        code === "account_locked"
          ? `Tu cuenta está bloqueada por demasiados intentos fallidos. Inténtalo de nuevo en ${espera}.`
          : `Demasiados intentos. Espera ${espera} antes de volver a intentarlo.`;
    }
    return { code, message, retryAfter };
  }

  if (status === 419 || data.error_code === "csrf") {
    return {
      code: "csrf",
      message: data.message || MENSAJES_POR_CODIGO.csrf,
      retryAfter: null,
    };
  }

  if (status === 401 || status === 403) {
    return {
      code: "session_load_failed",
      message: MENSAJES_POR_CODIGO.session_load_failed,
      retryAfter: null,
    };
  }

  if (status >= 500) {
    return {
      code: "server",
      message: MENSAJES_POR_CODIGO.server,
      retryAfter: null,
    };
  }

  if (data.error_code && MENSAJES_POR_CODIGO[data.error_code]) {
    return {
      code: data.error_code,
      message: data.message || MENSAJES_POR_CODIGO[data.error_code],
      retryAfter: null,
    };
  }

  const emailErr = primerErrorCampo(data.errors, "email");
  const codigoEmail = codigoDesdeErrorEmail(emailErr);
  if (codigoEmail) {
    return {
      code: codigoEmail,
      message: MENSAJES_POR_CODIGO[codigoEmail],
      retryAfter: null,
    };
  }

  if (status === 422) {
    const codeErr = primerErrorCampo(data.errors, "code");
    if (codeErr) {
      const locked =
        /bloquead/i.test(codeErr) || /lock/i.test(codeErr);
      return {
        code: locked ? "account_locked" : "two_factor_invalid",
        message: codeErr,
        retryAfter: null,
      };
    }
    const recoveryErr = primerErrorCampo(data.errors, "recovery_code");
    if (recoveryErr) {
      const locked =
        /bloquead/i.test(recoveryErr) || /lock/i.test(recoveryErr);
      return {
        code: locked ? "account_locked" : "two_factor_recovery_invalid",
        message: recoveryErr,
        retryAfter: null,
      };
    }
    if (emailErr && !codigoEmail) {
      return {
        code: "credentials",
        message: emailErr,
        retryAfter: null,
      };
    }
    return {
      code: "credentials",
      message: data.message || MENSAJES_POR_CODIGO.credentials,
      retryAfter: null,
    };
  }

  return {
    code: "unknown",
    message: data.message || MENSAJES_POR_CODIGO.unknown,
    retryAfter: null,
  };
}

export function mensajeLoginPorCodigo(code) {
  return MENSAJES_POR_CODIGO[code] || MENSAJES_POR_CODIGO.unknown;
}
